import React, { useState } from 'react';
import { Loader } from 'lucide-react';

function PhotoUploadModal({
  savedPattern,
  pattern,
  colors,
  fonts,
  user,
  supabase,
  onClose,
  onPhotosUploaded
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [captions, setCaptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 20 * 1024 * 1024) { // 20MB limit (compressed before upload)
        alert(`${file.name} is too large. Max 20MB per image.`);
        return false;
      }
      return true;
    });

    setSelectedFiles(validFiles);

    // Create preview URLs
    const newPreviews = validFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      id: Math.random().toString(36)
    }));
    setPreviews(newPreviews);
  };

  // Remove a photo from selection
  const handleRemovePhoto = (previewId) => {
    setPreviews(previews.filter(p => p.id !== previewId));
    setSelectedFiles(selectedFiles.filter((_, idx) => previews[idx].id !== previewId));

    // Remove caption if exists
    const newCaptions = { ...captions };
    delete newCaptions[previewId];
    setCaptions(newCaptions);
  };

  // Compress image before upload
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Max dimensions
          const maxWidth = 1920;
          const maxHeight = 1920;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.85);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Upload photos
  const handleUpload = async () => {
    if (previews.length === 0) {
      alert('Please select at least one photo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedPhotos = [];

      for (let i = 0; i < previews.length; i++) {
        const preview = previews[i];
        const file = preview.file;

        // Compress image
        const compressedFile = await compressImage(file);

        // Generate filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user.id}/${savedPattern.id}/${fileName}`;

        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('project-photos')
          .upload(filePath, compressedFile);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('project-photos')
          .getPublicUrl(filePath);

        // Save to database
        const { data: dbData, error: dbError } = await supabase
          .from('project_photos')
          .insert({
            user_id: user.id,
            saved_pattern_id: savedPattern.id,
            photo_url: urlData.publicUrl,
            caption: captions[preview.id] || null
          })
          .select()
          .single();

        if (dbError) throw dbError;

        uploadedPhotos.push(dbData);

        // Update progress
        setUploadProgress(Math.round(((i + 1) / previews.length) * 100));
      }

      alert(`✅ ${uploadedPhotos.length} photo${uploadedPhotos.length > 1 ? 's' : ''} uploaded!`);
      onPhotosUploaded(uploadedPhotos);
      onClose();

    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        padding: '2rem',
        overflow: 'auto'
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '20px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          fontFamily: fonts.body
        }}
      >
        {/* Header */}
        <div style={{
          padding: '2rem 2rem 1rem',
          borderBottom: `2px solid ${colors.borderGreige}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{
              fontSize: '1.8rem',
              fontFamily: fonts.display,
              color: colors.charcoal,
              marginBottom: '0.25rem'
            }}>
              Add Photos
            </h2>
            <p style={{ color: colors.darkGray, fontSize: '0.9rem' }}>
              {pattern.title}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: colors.brownGray,
              cursor: uploading ? 'not-allowed' : 'pointer',
              opacity: uploading ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* File Input */}
          {previews.length === 0 ? (
            <label
              style={{
                display: 'block',
                padding: '3rem',
                border: `3px dashed ${colors.borderGreige}`,
                borderRadius: '16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: colors.bgCream,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.sageGreen;
                e.currentTarget.style.background = `${colors.sageGreen}15`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.borderGreige;
                e.currentTarget.style.background = colors.bgCream;
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📸</div>
              <div style={{ fontSize: '1.1rem', color: colors.charcoal, fontWeight: '600', marginBottom: '0.5rem' }}>
                Click to select photos
              </div>
              <div style={{ fontSize: '0.85rem', color: colors.brownGray, marginTop: '0.5rem' }}>
                JPG, PNG up to 5MB each
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
          ) : (
            <>
              {/* Photo Previews */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {previews.map((preview, idx) => (
                  <div
                    key={preview.id}
                    style={{
                      position: 'relative',
                      background: colors.bgCream,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: `2px solid ${colors.borderGreige}`
                    }}
                  >
                    {/* Photo */}
                    <div style={{
                      position: 'relative',
                      paddingTop: '100%',
                      background: colors.borderGreige
                    }}>
                      <img
                        src={preview.url}
                        alt={`Preview ${idx + 1}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {idx === 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '0.5rem',
                          left: '0.5rem',
                          background: colors.mustard,
                          color: '#fff',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '50px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          Hero Image
                        </div>
                      )}
                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemovePhoto(preview.id)}
                        disabled={uploading}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          background: 'rgba(255,255,255,0.95)',
                          border: 'none',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          fontSize: '1.2rem',
                          color: colors.dustyRust,
                          opacity: uploading ? 0.5 : 1
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Caption Input */}
                    <div style={{ padding: '0.75rem' }}>
                      <input
                        type="text"
                        placeholder="Add caption (optional)"
                        value={captions[preview.id] || ''}
                        onChange={(e) => setCaptions({ ...captions, [preview.id]: e.target.value })}
                        disabled={uploading}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: `1px solid ${colors.borderGreige}`,
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                ))}

                {/* Add More Button */}
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    border: `2px dashed ${colors.borderGreige}`,
                    borderRadius: '12px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    background: colors.bgCream,
                    minHeight: '200px',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>+</div>
                  <div style={{ fontSize: '0.9rem', color: colors.darkGray, textAlign: 'center' }}>
                    Add More Photos
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      handleFileSelect({ target: { files: [...selectedFiles, ...newFiles] } });
                    }}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                </label>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div style={{
                  background: colors.bgCream,
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    color: colors.darkGray
                  }}>
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: colors.borderGreige,
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: colors.sageGreen,
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={onClose}
                  disabled={uploading}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: colors.borderGreige,
                    color: colors.charcoal,
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || previews.length === 0}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: uploading ? colors.borderGreige : colors.sageGreen,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {uploading ? (
                    <>
                      <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                      Uploading...
                    </>
                  ) : (
                    <>📸 Upload {previews.length} Photo{previews.length > 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PhotoUploadModal;
